import { Box, CircleDot, DollarSign, FileEdit, FolderTree, Image, Images, ListChecks, Menu, PersonStanding, Rotate3D, Sparkles, Sprout, TagIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, useToast } from "randmarcomps";
import { Product as ApiProduct, usePutV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuGenerateImageMutation } from "randmar-api-client";
import { Product } from "../../stores/product/Product";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Upload3DModelInput from "../media/Upload3DModelInput";
import AddInstantRebate from "./AddInstantRebate";
import AddProductToOpportunity from "./AddProductToOpportunity";
import AddToRequisition from "./AddToRequisition";
import DiscontinueProduct from "./DiscontinueProduct";
import EditCategory from "./EditCategory";
import EditPartNumber from "./EditPartNumber";
import EditProductImageWrapper from "./EditProductImageWrapper";
import EditStandardCost from "./EditStandardCost";
import EditState from "./EditState";
import ManageProductImagesDialog from "./ManageProductImagesDialog";
import Request3DScan from "./Request3DScan";

const isLegacyProduct = (product: Product | ApiProduct): product is Product => product instanceof Product;

interface ProductActionsProps {
  product: Product | ApiProduct;
}

export default function ProductActions({ product: productFromProps }: ProductActionsProps) {
  const product: Product = useMemo(() => {
    if (isLegacyProduct(productFromProps)) {
      return productFromProps;
    }

    const legacyProduct = new Product();
    Object.assign(legacyProduct, productFromProps);

    return legacyProduct;
  }, [productFromProps]);

  const [editStandardCostOpen, setEditStandardCostOpen] = useState(false);
  const [editPartNumberOpen, setEditPartNumberOpen] = useState(false);
  const [editCategoryOpen, setEditCategoryOpen] = useState(false);
  const [addInstantRebateOpen, setAddInstantRebateOpen] = useState(false);
  const [addProductToOpportunityOpen, setAddProductToOpportunityOpen] = useState(false);
  const [manageProductImagesOpen, setManageProductImagesOpen] = useState(false);
  const { applicationId } = useManufacturer();
  const { toast } = useToast();
  const [generateProductImage, { isLoading: isGeneratingProductImage }] =
    usePutV4ManufacturerByRouteManufacturerIdProductAndRandmarSkuGenerateImageMutation();

  const handleGenerateImage = async () => {
    if (!product.randmarSKU) {
      toast({ variant: "destructive", title: "Error", description: "Missing SKU." });
      return;
    }
    if (!applicationId) {
      toast({ variant: "destructive", title: "Error", description: "Missing manufacturer context." });
      return;
    }

    try {
      const wasQueued = await generateProductImage({ routeManufacturerId: applicationId, randmarSku: product.randmarSKU }).unwrap();
      if (wasQueued) {
        toast({
          variant: "success",
          title: "Queued",
          description: "Image generation queued. It may take a few minutes—refresh the page to see the update.",
        });
      } else {
        toast({ variant: "destructive", title: "Error", description: "Unable to queue image generation. Please try again." });
      }
    } catch (error) {
      console.error("Failed to queue product image generation:", error);
      toast({ variant: "destructive", title: "Error", description: "Unable to queue image generation. Please try again." });
    }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Menu className="mr-2 h-4 w-4" />
            <span>Actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <DropdownMenuLabel className="text-sidebar font-medium">Item Information</DropdownMenuLabel>
          <DropdownMenuGroup>
            <DropdownMenuItem onSelect={() => {
              setTimeout(() => setEditPartNumberOpen(true), 200);
            }}>
              <FileEdit className="mr-2 h-4 w-4" />
              <span>Edit Part Number - MPN</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => {
              setTimeout(() => setEditCategoryOpen(true), 200);
            }}>
              <FolderTree className="mr-2 h-4 w-4" />
              <span>Change Category</span>
            </DropdownMenuItem>
            <EditState product={product}>
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <CircleDot className="mr-2 h-4 w-4" />
                <span>Edit State</span>
              </DropdownMenuItem>
            </EditState>
            <DropdownMenuItem onSelect={() => {
              setTimeout(() => setEditStandardCostOpen(true), 200);
            }}>
              <DollarSign className="mr-2 h-4 w-4" />
              <span>Edit Standard Cost</span>
            </DropdownMenuItem>
            {product.state !== "Discontinued" &&
              <DiscontinueProduct product={product}>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  <PersonStanding className="mr-2 h-4 w-4" />
                  <span>Discontinue</span>
                </DropdownMenuItem>
              </DiscontinueProduct>
            }
          </DropdownMenuGroup>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-sidebar font-medium">Rebates</DropdownMenuLabel>
          <DropdownMenuGroup>
            <DropdownMenuItem onSelect={() => {
              setTimeout(() => setAddInstantRebateOpen(true), 200);
            }}>
              <TagIcon className="mr-2 h-4 w-4" />
              <span>Add Instant Rebate</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => {
              setTimeout(() => setAddProductToOpportunityOpen(true), 200);
            }}>
              <Sprout className="mr-2 h-4 w-4" />
              <span>Add to Opportunity</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-sidebar font-medium">Purchasing</DropdownMenuLabel>
          <DropdownMenuGroup>
            <AddToRequisition product={product}>
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <ListChecks className="mr-2 h-4 w-4" />
                <span>Add to Draft Purchase</span>
              </DropdownMenuItem>
            </AddToRequisition>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-sidebar font-medium">Utilities</DropdownMenuLabel>
          <DropdownMenuGroup>
            <DropdownMenuItem
              onSelect={() => {
                setTimeout(() => setManageProductImagesOpen(true), 200);
              }}
              disabled={!product.randmarSKU}
            >
              <Images className="mr-2 h-4 w-4" />
              <span>Manage Product Images</span>
            </DropdownMenuItem>
            <EditProductImageWrapper randmarSKU={product.randmarSKU}>
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <Image className="mr-2 h-4 w-4" />
                <span>Upload Product Image</span>
              </DropdownMenuItem>
            </EditProductImageWrapper>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                void handleGenerateImage();
              }}
              disabled={isGeneratingProductImage}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              <span>{isGeneratingProductImage ? "Queuing Image…" : "Generate Product Image"}</span>
            </DropdownMenuItem>
            <Upload3DModelInput randmarSKU={product.randmarSKU}>
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <Box className="mr-2 h-4 w-4" />
                <span>Upload 3D Model</span>
              </DropdownMenuItem>
            </Upload3DModelInput>
            {product.require3DScan === false &&
              <Request3DScan product={product}>
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  <Rotate3D className="mr-2 h-4 w-4" />
                  <span>Request 3D Scan</span>
                </DropdownMenuItem>
              </Request3DScan>}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <EditStandardCost
        product={product}
        open={editStandardCostOpen}
        onOpenChange={setEditStandardCostOpen}
      />
      <EditPartNumber
        product={product}
        open={editPartNumberOpen}
        onOpenChange={setEditPartNumberOpen}
      />
      <EditCategory
        product={product}
        open={editCategoryOpen}
        onOpenChange={setEditCategoryOpen}
      />
      <AddInstantRebate
        product={product}
        open={addInstantRebateOpen}
        onOpenChange={setAddInstantRebateOpen}
      />
      <AddProductToOpportunity
        product={product}
        open={addProductToOpportunityOpen}
        onOpenChange={setAddProductToOpportunityOpen}
      />
      <ManageProductImagesDialog
        randmarSKU={product.randmarSKU}
        open={manageProductImagesOpen}
        onOpenChange={setManageProductImagesOpen}
      />
    </>
  )
}
